import cn from 'classnames';
import React, { RefObject, useMemo } from 'react';

import type { BBCommonProps, BBEditableModeProps } from 'types/story';
import cms from 'utils/cms';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { useClientSelector } from 'client/reducers';
import { selectCmsItems } from 'client/reducers/story/selectors';

import { useGptSlot } from './hooks';

import css from './AdUnit.scss';

type AdUnitParams = {
	path: string;
	targeting?: string;
	sizes: googletag.GeneralSize;
	isFluidSize: boolean;
	slotWidthPx: number;
	slotHeightPx: number;
};
type Props = BBCommonProps;

/**
 * Ad Unit component for rendering Google Ad Manager (GPT) which showing ad slots
 *
 *
 */
const AdUnit: React.FC<Props> = props => {
	const { uiConfig, stateAttrs, eventListeners, containerRef, isEditableMode, editableModeProps } = props;
	const cmsItems = useClientSelector(selectCmsItems);

	// Get the ad path value from props
	const adUnitParams: AdUnitParams = useMemo(() => {
		const path = uiConfig.componentProps?.other?.adUnitPath ?? '';
		const width = uiConfig.componentProps?.other?.adUnitWidth ?? '';
		const height = uiConfig.componentProps?.other?.adUnitHeight ?? '';
		const isFluidSize = uiConfig.componentProps?.other?.adUnitIsFluidSize ?? false;
		const targeting = uiConfig.componentProps?.other?.adUnitTargeting ?? '';
		const slotWidthPx = Number(cms.resolveCmsReferenceValue(width, cmsItems));
		const slotHeightPx = Number(cms.resolveCmsReferenceValue(height, cmsItems));

		return {
			path: cms.resolveCmsReferenceValue(path, cmsItems),
			targeting: cms.resolveCmsReferenceValue(targeting, cmsItems),
			sizes: isFluidSize ? ['fluid'] : [slotWidthPx, slotHeightPx],
			isFluidSize,
			slotWidthPx,
			slotHeightPx,
		};
	}, [
		cmsItems,
		uiConfig.componentProps?.other?.adUnitPath,
		uiConfig.componentProps?.other?.adUnitIsFluidSize,
		uiConfig.componentProps?.other?.adUnitWidth,
		uiConfig.componentProps?.other?.adUnitHeight,
		uiConfig.componentProps?.other?.adUnitTargeting,
	]);

	useGptSlot(uiConfig.nodeProps.id, adUnitParams.path, adUnitParams.sizes, !isEditableMode, adUnitParams.targeting);

	// Render the component with the given props
	const renderContent = (additionalProps: Partial<BBEditableModeProps['nodeProps']> = {}) => {
		const { nodeProps } = uiConfig;
		const { style: nodeStyle, ...restNodeProps } = nodeProps;
		const { style: additionalStyle, ...restAdditionalProps } = additionalProps;
		const className = cn(css.adUnit, nodeProps.className, additionalProps.className, {
			[css.empty]: isEditableMode,
		});

		const hasValidFixedSlot =
			!adUnitParams.isFluidSize &&
			Number.isFinite(adUnitParams.slotWidthPx) &&
			Number.isFinite(adUnitParams.slotHeightPx) &&
			adUnitParams.slotWidthPx > 0 &&
			adUnitParams.slotHeightPx > 0;

		const fixedSlotStyle: React.CSSProperties | undefined = hasValidFixedSlot
			? {
					width: adUnitParams.slotWidthPx,
					height: adUnitParams.slotHeightPx,
				}
			: undefined;

		return (
			<div
				{...restNodeProps}
				{...stateAttrs}
				{...eventListeners}
				{...restAdditionalProps}
				className={className}
				style={{
					...(typeof nodeStyle === 'object' && nodeStyle ? nodeStyle : {}),
					...(typeof additionalStyle === 'object' && additionalStyle ? additionalStyle : {}),
					...fixedSlotStyle,
				}}
				ref={containerRef as RefObject<HTMLDivElement>}
			/>
		);
	};

	// In editable mode, we need to add additional props from editableModeProps
	if (isEditableMode) {
		const editableProps = { ...editableModeProps?.nodeProps };
		return renderContent(editableProps);
	}

	// Default rendering
	return renderContent();
};

export default withCardTransitionContext(AdUnit);
